import type { HttpAdapter } from "./adapters/http-adapter.ts";
import type { LoggerAdapter } from "./adapters/logger-adapter.ts";
import {
  StorageQuotaExceededError,
  type StorageAdapter,
} from "./adapters/storage-adapter.ts";
import { Buffer } from "./buffer.ts";
import { Mutex, MutexDisposedError } from "./mutex.ts";
import type { TelemetryHooks } from "./telemetry.ts";
import type { Event } from "./types.ts";
import { calculateBackoff, delay, DelayAbortedError } from "./utils.ts";

/**
 * Batching configuration.
 */
export type BatchOptions = {
  /**
   * Interval in milliseconds between automatic flushes (default: `10000`).
   */
  interval?: number;
  /**
   * Maximum number of events per batch before auto-flush (default: `10`).
   */
  size?: number;
  /**
   * Maximum total serialized payload size in bytes per batch before auto-flush (default: `65536` — 64 KB).
   */
  maxPayloadSize?: number;
};

/**
 * Retry configuration.
 */
export type RetryOptions = {
  /**
   * Maximum number of retry attempts per request (default: `3`).
   */
  maxAttempts?: number;
  /**
   * Minimum delay in milliseconds before the first retry (default: `1000`).
   */
  minDelay?: number;
  /**
   * Maximum delay in milliseconds between retries (default: `360000`).
   */
  maxDelay?: number;
  /**
   * Multiplicative factor applied to the delay on each retry (default: `2`).
   */
  backoffFactor?: number;
};

/**
 * Configuration for the Dispatcher.
 */
export type DispatcherConfig = {
  /**
   * API key for authentication
   */
  apiKey: string;
  /**
   * Header name for API key
   */
  apiKeyHeader: string;
  /**
   * API endpoint URL
   */
  endpoint: string;
  /**
   * Batching options
   */
  batchOptions: Required<BatchOptions>;
  /**
   * Retry options
   */
  retryOptions: Required<RetryOptions>;
  /**
   * Logger for internal logging
   */
  logger: LoggerAdapter;
  /**
   * Maximum size of the in-memory event buffer (optional).
   * When limit is exceeded, oldest events are evicted using FIFO policy.
   */
  maxBufferSize: number;
  /**
   * Per-event time-to-live in milliseconds.
   * Events older than this are filtered out at flush time.
   * `null` means no expiry.
   */
  eventTtl: number | null;
  /**
   * Telemetry hooks for monitoring.
   */
  hooks: TelemetryHooks;
};

/**
 * Manages event queuing, batching, flushing, and retry logic.
 * Automatically flushes events based on batch size or time interval.
 * Implements exponential backoff with jitter for retries.
 *
 * @template TMetadata The type of metadata attached to events
 */
export class Dispatcher<
  TMetadata extends Record<string, unknown> = Record<string, unknown>,
> {
  #buffer!: Buffer<Event<TMetadata>>;
  #flushMutex = new Mutex();
  #storageMutex = new Mutex();
  #retryAbortController = new AbortController();

  #timer: ReturnType<typeof setTimeout> | null = null;

  #disposed = false;

  readonly #config: DispatcherConfig;
  readonly #httpClient: HttpAdapter;
  readonly #storage: StorageAdapter;
  readonly #logger: LoggerAdapter;

  /**
   * Create a new Dispatcher instance.
   *
   * @param config Dispatcher configuration
   * @param httpClient HTTP adapter for sending events
   * @param storage Storage adapter for persisting events
   */
  constructor(
    config: DispatcherConfig,
    httpClient: HttpAdapter,
    storage: StorageAdapter,
  ) {
    // Validate configuration
    if (config.maxBufferSize < config.batchOptions.size) {
      throw new Error(
        `Invalid configuration: maxBufferSize (${config.maxBufferSize}) must be >= batchOptions.size (${config.batchOptions.size}). ` +
          `The batch size will never be reached and events will be dropped unnecessarily.`,
      );
    }

    this.#config = config;
    this.#httpClient = httpClient;
    this.#storage = storage;
    this.#logger = config.logger;
    this.#buffer = new Buffer<Event<TMetadata>>(config.maxBufferSize);
  }

  /**
   * Add an event to the buffer.
   * Triggers auto-flush if batch size threshold is reached.
   *
   * @param event The event to enqueue
   */
  public async enqueue(event: Event<TMetadata>): Promise<void> {
    if (this.#disposed) {
      this.#logger.warn("Cannot enqueue event: Dispatcher has been disposed");

      return;
    }

    this.#buffer.enqueue(event);
    this.#config.hooks.onEnqueue?.({ bufferSize: this.#buffer.size() });

    // Fire-and-forget: the in-memory buffer is the source of truth here.
    // Persistence is a best-effort durability snapshot; callers shouldn't
    // block on disk I/O in the hot path.
    void this.#persistBuffer();

    if (this.#buffer.size() >= this.#config.batchOptions.size) {
      await this.flush();
    } else {
      this.#scheduleFlush();
    }
  }

  /**
   * Immediately flush all queued events.
   * Cancels any scheduled flush.
   * Uses mutex to prevent concurrent flush operations.
   */
  public async flush(): Promise<void> {
    /*
      Drains the buffer incrementally via dequeue(), building and sending one
      batch at a time. This streaming approach avoids allocating intermediate
      arrays (no toArray + filterExpired + createBatches), keeping peak memory
      at ~1 batch size regardless of total buffer contents.
    */
    await this.#flushMutex.runAtomic(async () => {
      if (this.#timer) {
        clearTimeout(this.#timer);
        this.#timer = null;
      }

      if (this.#buffer.isEmpty()) return;

      const { size, maxPayloadSize } = this.#config.batchOptions;
      const ttl = this.#config.eventTtl;
      const now = ttl !== null ? Date.now() : 0;

      let totalEvents = 0;
      let totalBatches = 0;
      let droppedCount = 0;

      let batch: Event<TMetadata>[] = [];
      let batchPayloadSize = 0;

      // Drain the buffer one event at a time. For each event:
      // 1. Filter expired events inline (no separate pass)
      // 2. Check if adding it would overflow the current batch
      // 3. If overflow: send the current batch, then start a new one
      while (!this.#buffer.isEmpty()) {
        const event = this.#buffer.dequeue()!;

        // Inline TTL filtering — skip expired events without allocating a filtered array
        if (ttl !== null && now - event.issuedAt > ttl) {
          droppedCount++;

          continue;
        }

        const eventSize = JSON.stringify(event).length;

        // Batch boundary: send current batch when adding this event would
        // exceed either the count limit or payload size limit
        if (
          batch.length > 0 &&
          (batch.length >= size ||
            batchPayloadSize + eventSize > maxPayloadSize)
        ) {
          totalEvents += batch.length;
          totalBatches++;

          const success = await this.#sendWithRetry(batch);

          if (!success) {
            // On failure, requeue the failed batch + the current event (already
            // dequeued but not yet in any batch). Remaining buffer events are
            // preserved since they haven't been dequeued yet.
            await this.#requeueBatch([...batch, event]);

            return;
          }

          batch = [];
          batchPayloadSize = 0;
        }

        batch.push(event);
        batchPayloadSize += eventSize;
      }

      if (droppedCount > 0) {
        this.#config.hooks.onDrop?.({
          eventCount: droppedCount,
          reason: "expired",
        });
      }

      // Send the trailing batch (events that didn't trigger an overflow)
      if (batch.length > 0) {
        totalEvents += batch.length;
        totalBatches++;

        this.#config.hooks.onFlush?.({
          eventCount: totalEvents,
          batchCount: totalBatches,
        });

        const success = await this.#sendWithRetry(batch);

        if (!success) {
          await this.#requeueBatch(batch);

          return;
        }
      }
    });
  }

  /**
   * Send events with exponential backoff retry logic.
   *
   * @param events Events to send
   * @param attempt Current retry attempt number
   * @returns Whether the send was successful (or non-retryable)
   */
  async #sendWithRetry(
    events: Event<TMetadata>[],
    attempt: number = 0,
  ): Promise<boolean> {
    try {
      const response = await this.#httpClient.send({
        events,
        endpoint: this.#config.endpoint,
        headers: {
          [this.#config.apiKeyHeader]: this.#config.apiKey,
          "Content-Type": "application/json",
        },
        apiKeyHeader: this.#config.apiKeyHeader,
      });

      return await this.#handleResponse(response, events, attempt);
    } catch (err) {
      return await this.#handleNetworkError(err, events, attempt);
    }
  }

  /**
   * Handle HTTP response based on status code.
   *
   * @param response HTTP response
   * @param events Events that were sent
   * @param attempt Current retry attempt
   * @returns Whether the request was successful (or non-retryable)
   */
  async #handleResponse(
    response: { status: number },
    events: Event<TMetadata>[],
    attempt: number,
  ): Promise<boolean> {
    if (response.status >= 200 && response.status < 300) {
      // Awaited: storage must reflect the drained buffer before flush
      // completes, otherwise a crash + restore would re-send these events.
      await this.#persistBuffer();

      this.#config.hooks.onSendSuccess?.({
        batchSize: events.length,
        status: response.status,
      });

      return true;
    } else if (response.status >= 400 && response.status < 500) {
      this.#logger.warn("4xx client error, dropping events", {
        status: response.status,
        eventsCount: events.length,
      });

      await this.#persistBuffer();

      this.#config.hooks.onDrop?.({
        eventCount: events.length,
        reason: "client_error",
      });

      return true;
    } else if (response.status >= 500) {
      return await this.#handleServerError(response.status, events, attempt);
    } else {
      // 1xx, 3xx: Unexpected status codes, treat as client error and drop
      this.#logger.warn("Unexpected status code, dropping events", {
        status: response.status,
        eventsCount: events.length,
      });

      await this.#persistBuffer();

      return true;
    }
  }

  /**
   * Handle 5xx server errors with retry logic.
   *
   * @param status HTTP status code
   * @param events Events to retry
   * @param attempt Current retry attempt
   * @returns Whether the retry eventually succeeded
   */
  async #handleServerError(
    status: number,
    events: Event<TMetadata>[],
    attempt: number,
  ): Promise<boolean> {
    if (attempt < this.#config.retryOptions.maxAttempts) {
      this.#logger.warn("5xx server error, retrying", {
        status,
        attempt: attempt + 1,
        maxRetries: this.#config.retryOptions.maxAttempts,
      });

      try {
        const backoffDelay = calculateBackoff(
          attempt,
          this.#config.retryOptions.minDelay,
          this.#config.retryOptions.maxDelay,
          this.#config.retryOptions.backoffFactor,
        );

        this.#config.hooks.onRetry?.({
          attempt: attempt + 1,
          delay: backoffDelay,
        });

        await delay(backoffDelay, this.#retryAbortController.signal);

        return await this.#sendWithRetry(events, attempt + 1);
      } catch (err) {
        /* v8 ignore next -- @preserve */
        if (err instanceof DelayAbortedError) return false;

        /* v8 ignore next -- @preserve */
        throw err;
      }
    } else {
      this.#logger.error("5xx server error, max retries reached", {
        status,
        maxRetries: this.#config.retryOptions.maxAttempts,
        eventsCount: events.length,
      });

      this.#config.hooks.onSendFailure?.({
        batchSize: events.length,
        error: `5xx: ${status}`,
        attempt,
      });

      return false;
    }
  }

  /**
   * Handle network errors with retry logic.
   *
   * @param err Error that occurred
   * @param events Events to retry
   * @param attempt Current retry attempt
   * @returns Whether the retry eventually succeeded
   */
  async #handleNetworkError(
    err: unknown,
    events: Event<TMetadata>[],
    attempt: number,
  ): Promise<boolean> {
    /* v8 ignore next -- @preserve */
    if (err instanceof DelayAbortedError) return false;

    this.#logger.error("Network error occurred", { err });

    if (attempt < this.#config.retryOptions.maxAttempts) {
      this.#logger.warn("Network error, retrying", {
        attempt: attempt + 1,
        maxRetries: this.#config.retryOptions.maxAttempts,
        error: err instanceof Error ? err.message : String(err),
      });

      try {
        const backoffDelay = calculateBackoff(
          attempt,
          this.#config.retryOptions.minDelay,
          this.#config.retryOptions.maxDelay,
          this.#config.retryOptions.backoffFactor,
        );

        this.#config.hooks.onRetry?.({
          attempt: attempt + 1,
          delay: backoffDelay,
        });

        await delay(backoffDelay, this.#retryAbortController.signal);

        return await this.#sendWithRetry(events, attempt + 1);
      } catch (delayErr) {
        /* v8 ignore next -- @preserve */
        if (delayErr instanceof DelayAbortedError) return false;

        /* v8 ignore next -- @preserve */
        throw delayErr;
      }
    } else {
      this.#logger.error("Network error, max retries reached", {
        maxRetries: this.#config.retryOptions.maxAttempts,
        eventsCount: events.length,
        /* v8 ignore next -- @preserve */
        error: err instanceof Error ? err.message : String(err),
      });

      this.#config.hooks.onSendFailure?.({
        batchSize: events.length,
        /* v8 ignore next -- @preserve */
        error: err instanceof Error ? err.message : String(err),
        attempt,
      });

      return false;
    }
  }

  /**
   * Persist current buffer state to storage.
   * Serialized via mutex to prevent stale overwrites.
   */
  async #persistBuffer(): Promise<void> {
    try {
      await this.#storageMutex.runAtomic(async () => {
        await this.#storage.save(this.#buffer.toArray());
      });
    } catch (err) {
      /* v8 ignore next -- @preserve */
      if (err instanceof MutexDisposedError) return;

      if (err instanceof StorageQuotaExceededError) {
        this.#logger.warn(err.message);
      } else {
        this.#logger.error("Failed to persist events to storage", {
          error: err instanceof Error ? err.message : String(err),
          queueSize: this.#buffer.size(),
        });
      }
    }
  }

  /**
   * Re-queue a failed batch plus any remaining buffered events and persist to storage.
   *
   * @param batch The failed batch to re-queue
   * @param errorMessage Error message to log if save fails
   */
  async #requeueBatch(batch: Event<TMetadata>[]): Promise<void> {
    const merged = [...batch, ...this.#buffer.toArray()];

    this.#buffer.fromArray(merged);

    await this.#persistBuffer();
  }

  /**
   * Schedule an automatic flush after the configured interval.
   * Does nothing if a flush is already scheduled.
   */
  #scheduleFlush(): void {
    if (this.#disposed || this.#timer) return;

    this.#timer = setTimeout(() => {
      void this.flush();
    }, this.#config.batchOptions.interval);
  }

  /**
   * Reset internal state for reinitialization after disposal.
   */
  #reset(): void {
    this.#disposed = false;
    this.#flushMutex.reset();
    this.#storageMutex.reset();
    this.#retryAbortController = new AbortController();
  }

  /**
   * Restore persisted events from storage.
   * Called during initialization to recover unsent events.
   */
  public async restore(): Promise<void> {
    this.#reset();

    try {
      const stored = await this.#storage.load();

      this.#buffer.fromArray(stored as Event<TMetadata>[]);

      if (this.#buffer.size() > 0) {
        this.#scheduleFlush();
      }
    } catch (err) {
      this.#logger.error("Failed to restore events from storage", {
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }

  /**
   * Clean up resources, cancel scheduled flushes, and clear all references for memory cleanup.
   * Should be called when disposing the client.
   */
  public dispose(): void {
    this.#disposed = true;
    this.#retryAbortController.abort();

    if (this.#timer) {
      clearTimeout(this.#timer);
      this.#timer = null;
    }

    this.#buffer.clear();
    this.#flushMutex.release();
    this.#storageMutex.release();
    void this.#storage.close();
  }
}
